import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import StopIcon from '@mui/icons-material/Stop';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockChip from '../components/GuideMockChip';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideProjectDetail: React.FC = () => {
  return (
    <GuidePageLayout
      title="プロジェクトの詳細情報を確認する"
      description="プロジェクトの登録内容を詳細に確認します（取引先・マネージャー情報含む）。"
      breadcrumbs={[{ label: 'プロジェクト管理', path: '/guide/project/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: 'プロジェクト管理画面を開く' },
            { label: 'プロジェクト一覧から対象プロジェクトの行をクリックする' },
            {
              label: '「プロジェクト詳細」が右パネルに表示される',
              description:
                'プロジェクトの全情報が参照専用で表示されます。ヘッダー右上にIDとコピーボタンが表示されます。',
              content: (
                <GuideMockContainer label="プロジェクト詳細（参照モード）">
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        プロジェクト詳細
                      </Typography>
                      <GuideMockChip label="稼働中" color="success" />
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          ID: xxxxxxxx-xxxx-xxxx-xxxx
                        </Typography>
                        <IconButton size="small">
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="表示される情報">
        <GuideTable
          headers={['カテゴリ', '項目']}
          rows={[
            ['基本情報', 'プロジェクト名、課長名、取引先名'],
            ['補足情報', '規模、商流、業界（Chip一覧）'],
            ['技術スタック', '技術レイヤー・技術種別にカテゴリ分類されたChip一覧'],
            ['期間', '開始日、終了日（yyyy/mm/dd形式）'],
            ['メタ情報', '作成者/作成日、更新者/更新日'],
            ['備考', '自由入力メモ'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '動作']}
          rows={[
            ['← 戻る矢印', '詳細画面を閉じて一覧に戻る'],
            ['IDコピーボタン', 'プロジェクトIDをクリップボードにコピー'],
            ['編集ボタン', '編集モードに切り替える（ステータスが「終了」の場合は非表示）'],
            [
              'プロジェクト終了ボタン',
              'プロジェクトを終了する（確認ダイアログで終了日を入力）。ステータスが「終了」の場合は非表示',
            ],
          ]}
        />
        <Stack sx={{ mt: 1 }}>
          <GuideMockContainer label="フッターボタン（終了ステータスでない場合のみ表示）">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <GuideMockButton label="プロジェクト終了" color="success" startIcon={<StopIcon />} />
              <GuideMockButton label="編集" startIcon={<EditIcon />} />
            </Stack>
          </GuideMockContainer>
        </Stack>
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>
            ステータスが「終了」のプロジェクトは参照のみ可能で、編集ボタンは表示されません。
          </GuideInfoBox>
          <GuideInfoBox>
            登録者・更新者が削除済みユーザーの場合、名前の代わりにユーザーIDが表示されます。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideProjectDetail;
